import { axios } from 'Fetcher'
import { SHAHashValue } from 'Lib/Crypto'
import { Selectors } from 'Reducers/Selectors'
import { Store } from 'redux'
import nullthrows from 'nullthrows'

const PIXEL_ID = 'o2tn3'
const TWITTER_SCRIPT_ID = 'twitter-pixel'
const TWITTER_SCRIPT_SRC = 'https://static.ads-twitter.com/uwt.js'
const XAPI_ROUTING_PATH = '/api/collect/xapi'
const DISABLE_ON_DEV = true

interface PixelEvent {
  conversion_id: string // For deduplication. See https://business.twitter.com/en/help/campaign-measurement-and-analytics/conversion-tracking-for-websites.html
  currency: string
  value: number
  contents?: Object[]
  description: string
  status: 'started' | 'completed' // See "Event Parameters" section in https://business.twitter.com/en/help/campaign-measurement-and-analytics/conversion-tracking-for-websites.html
  twclid?: string | null
}

type ConversionsEvent = PixelEvent & {
  email?: string | null
}

if (!window.twq) {
  const twitterEventTracker: any = (window.twq = (...args: any) => {
    twitterEventTracker.exe
      ? twitterEventTracker.exe.apply(twitterEventTracker, args)
      : twitterEventTracker.queue.push(args)
  })
  twitterEventTracker.version = '1.1'
  twitterEventTracker.queue = []
  window.twq('config', PIXEL_ID)
}

const getTwitterScript = () => document.querySelector(`#${TWITTER_SCRIPT_ID}`)

let store: Store | null = null

export const TWITTER_EVENTS = {
  PURCHASE: 'r4njx'
}

export const initTwitterService = (storeInstance: Store) => {
  store = storeInstance

  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  if (getTwitterScript()) {
    return // Already initialized
  }

  const head = document.querySelector('head')
  const twitterScript = document.createElement('script')
  twitterScript.type = 'text/javascript'
  twitterScript.async = true
  twitterScript.src = TWITTER_SCRIPT_SRC
  twitterScript.id = TWITTER_SCRIPT_ID
  head?.appendChild(twitterScript) // This one runs the script
}

export const revokeTwitterConsent = () => {
  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  getTwitterScript()?.remove() // Remove script element from DOM
}

const logConversionsEvent = async (eventId: string, params: ConversionsEvent) => {
  try {
    if (__DEV__ && DISABLE_ON_DEV) {
      return
    }

    const hashedEmail = await SHAHashValue(params.email)

    // Error handling is done on the backend side
    await axios.post(XAPI_ROUTING_PATH, {
      event_id: eventId,
      identifiers: {
        hashed_email: hashedEmail,
        twclid: params.twclid,
        user_agent: navigator.userAgent,
      },
      value: params.value.toFixed(2),
      price_currency: params.currency,
      description: params.description,
      status: params.status,
      conversion_id: params.conversion_id,
      number_items: params.contents?.length ?? 0,
      contents: params.contents ?? []
    })
  } catch (error) {
    throw error?.response?.data ?? error?.response ?? error
  }
}

export const trackTwitterEvent = (eventId: string, params: PixelEvent) => {
  try {
    if (__DEV__ && DISABLE_ON_DEV) {
      return
    }

    if (!window.twq) {
      return
    }

    const state = nullthrows(store).getState()
    const trackedEmail = Selectors.getTrackedEmail(state)

    const updatedParams = {
      ...params,
      email: trackedEmail
    }

    window.twq('event', eventId, updatedParams)
    void logConversionsEvent(eventId, updatedParams)
  } catch (error) {
    logError('web_twitter_trackError', error, { eventId }, true)
  }
}
